import React from "react";
import { Button } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiEmailOutline, mdiMessageTextOutline } from "@mdi/js";
import CardShell from "./CardShell";
import JobStatusBadge from "app/components/common/JobStatusBadge";
import { REPORT_BUG_HELP_LINK } from "app/constants/global-constants";
import { formatDateTimeLong } from "app/containers/Jobs/utils/dateFormat";

// Per-step action: "Send Now" for a ready step, "Copy Content" when a step
// failed or was canceled (so the inspector can send manually). Deferred to the
// Email Automation feature for real wiring.
//
// Because Send Now is still a placeholder, a `ready_to_send` step is a dead end
// on this page: nothing has reached the client and nothing here can make it.
// The badge alone reads as progress ("Ready to Send"), so the row states the
// consequence in words instead of leaving the inspector to infer it.
const stepAction = (status) => {
  if (status === "ready_to_send")
    return { label: "Send Now", variant: "primary", key: "Send email" };
  if (status === "failed" || status === "canceled")
    return {
      label: "Copy Content",
      variant: "outline-primary",
      key: "Copy email content",
    };
  return null;
};

const EmailAutomationCard = ({ job, onDeferred }) => {
  const steps = (job.emails || []).filter((step) => step.status);

  return (
    <CardShell
      icon={<Icon path={mdiEmailOutline} size={0.7} className="jd-card-icon" />}
      title="Email Automation"
    >
      {steps.length === 0 ? (
        <div className="jd-subtle">No emails scheduled for this job.</div>
      ) : (
        <ul className="jd-email-list">
          {steps.map((step) => {
            const action = stepAction(step.status);
            const isFailed = step.status === "failed";
            // A step that has neither sent nor been scheduled has nothing to
            // show here. Each of these owns a grid row on mobile, so rendering
            // an empty one costs a visible gap — omit them instead.
            const isReady = step.status === "ready_to_send";
            const meta = isReady
              ? "Not emailed yet"
              : step.description || formatDateTimeLong(step.timestamp);
            return (
              <li key={step.id} className="jd-email-row">
                <span className="jd-email-name">{step.name}</span>
                <JobStatusBadge domain="email" value={step.status} />
                {meta && (
                  <span
                    className={
                      isReady
                        ? "jd-email-meta jd-email-meta--warn"
                        : "jd-email-meta"
                    }
                  >
                    {meta}
                  </span>
                )}
                {(action || isFailed) && (
                  <span className="jd-email-action">
                    {isFailed && (
                      <Button
                        variant="link"
                        size="sm"
                        className="jd-email-report-issue"
                        aria-label="Report issue"
                        title="Report an issue"
                        onClick={() =>
                          window.open(
                            REPORT_BUG_HELP_LINK,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        <Icon path={mdiMessageTextOutline} size={0.7} />
                      </Button>
                    )}
                    {action && (
                      <Button
                        variant={action.variant}
                        size="sm"
                        onClick={() => onDeferred(action.key)}
                      >
                        {action.label}
                      </Button>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </CardShell>
  );
};

export default EmailAutomationCard;
